import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import Delete from "@mui/icons-material/Delete";
import { styled } from "@mui/system";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fetchHealthFacilitiesWithProductBundle } from "../../action";
import TableN from "../../reusableComponents/components/generics/TableN";
import _ from "lodash";

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  "& .MuiAccordionSummary-content": {
    margin: "0px !important",
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  ...theme.paper.header,
  fontSize: 600,
  padding: ".8rem",
}));

const MEDECINE_ACT_TYPE_LABEL = "medecine";

export const PreAuthMedical = ({
  onDataChange,
  claimData,
  updateTotalClaimed,
  updateTMvalue,
  disabled,
  updateSupportTotal,
}) => {
  const SignInSuccess = useSelector((state) => state);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [rows, setRows] = useState([
    {
      productId: "",
      item: "",
      quantity: "",
      price: "",
      tmValue: "",
      countedPrice: "",
      explanation: "",
      priceAsked: "",
    },
  ]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [totalClaimed, setTotalClaimed] = useState(0);

  const healthData =
    SignInSuccess?.fosaReducer?.pickerHealthFacilityInsurees?.[0];
  const healthFacilityUuid = claimData?.healthFacility?.uuid || healthData?.uuid;
  const healthSupport =
    SignInSuccess?.fosaReducer?.healthFacilityHealthSupport ?? 100;

  useEffect(() => {
    if (healthFacilityUuid) {
      dispatch(
        fetchHealthFacilitiesWithProductBundle(healthFacilityUuid, [
          "first:50",
        ])
      );
    }
  }, [dispatch, healthFacilityUuid]);

  useEffect(() => {
    if (SignInSuccess?.fosaReducer?.healthFacilitiesProductBundleData) {
      const products =
        SignInSuccess.fosaReducer.healthFacilitiesProductBundleData?.filter((product) => product?.productM2m?.apStatus === "REQUIS");
      setFilteredServices(products || []);
    }
  }, [SignInSuccess?.fosaReducer?.healthFacilitiesProductBundleData]);

  /* eslint-disable react-hooks/exhaustive-deps -- onDataChange from parent; avoid resetting rows on re-render */
  useEffect(() => {
    const healthProducts =
      claimData?.claimProducts ?? claimData?.paProducts ?? [];
    const legacyItems = claimData?.items ?? [];
    const productSource =
      healthProducts.length > 0 ? healthProducts : legacyItems;
    const isClaimProducts = healthProducts.length > 0;

    const medicinePaActs = (claimData?.paActs ?? []).filter(
      (actItem) =>
        (actItem?.act?.actType?.label || "").toLowerCase() ===
        MEDECINE_ACT_TYPE_LABEL.toLowerCase()
    );

    const productRows =
      productSource.length > 0
        ? productSource.map((itemRow) => {
          const jsonExt = JSON.parse(
            itemRow.jsonExt || itemRow.json_ext || "{}"
          );
          const product = isClaimProducts
            ? itemRow.healthProduct
            : itemRow.item;
          const fullCode =
            product?.fullCode ||
            product?.full_code ||
            product?.code ||
            product?.label ||
            "";
          const displayName =
            fullCode || product?.name || product?.label || "";
          const productId = product?.id ?? product?.uuid;
          const price =
            itemRow.priceAsked ??
            product?.priceHomologated ??
            product?.price_homologated;
          return {
            productId: productId != null ? String(productId) : "",
            item: displayName,
            price,
            quantity: itemRow.qtyProvided,
            priceAsked: itemRow.priceAsked,
            countedPrice: jsonExt.countedPrice,
            tmValue: jsonExt.tmValue,
            explanation: itemRow.explanation,
            isMedicineAct: false,
          };
        })
        : [];

    const medicineActRows = medicinePaActs.map((actItem) => {
      const jsonExt = JSON.parse(
        actItem.jsonExt || actItem.json_ext || "{}"
      );
      const act = actItem.act;
      const displayName =
        act?.fullCode ||
        act?.full_code ||
        act?.code ||
        act?.label ||
        "";
      return {
        productId: "",
        actId: act?.uuid || act?.id,
        item: displayName,
        price:
          actItem.priceAsked ||
          act?.priceHomologated ||
          act?.price_homologated,
        quantity: actItem.qtyProvided,
        priceAsked: actItem.priceAsked,
        countedPrice: jsonExt.countedPrice,
        tmValue: jsonExt.tmValue,
        explanation: actItem.explanation,
        isMedicineAct: true,
      };
    });

    const initialRows =
      productRows.length > 0 || medicineActRows.length > 0
        ? [...productRows, ...medicineActRows]
        : [];

    if (initialRows.length > 0) {
      setRows(initialRows);
      setSelectedServices(
        initialRows
          .filter((row) => !row.isMedicineAct)
          .map((row) => row.productId)
          .filter(Boolean)
      );
      calculateTotalClaimed(initialRows);
      onDataChange(initialRows.filter((row) => !row.isMedicineAct));
    } else {
      setRows([
        {
          productId: "",
          item: "",
          quantity: "",
          price: "",
          tmValue: "",
          countedPrice: "",
          explanation: "",
          priceAsked: "",
        },
      ]);
    }
  }, [claimData]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleServiceChange = (index, option) => {
    if (!option || option.id == null || option.id === "") return;

    const optionIdStr = String(option.id);
    const selectedProduct = filteredServices.find(
      (item) =>
        getProductId(item) != null &&
        String(getProductId(item)) === optionIdStr
    );
    if (!selectedProduct?.productM2m) return;

    const productM2m = selectedProduct.productM2m;
    const price = Number(
      selectedProduct.agreedPrice ?? productM2m.priceHomologated ?? 0
    );
    const healthSupportPct = Number(healthSupport) || 100;
    const countedPricePerUnit = _.round(
      price * (healthSupportPct / 100),
      2
    );
    const tmValuePerUnit = _.round(
      price * ((100 - healthSupportPct) / 100),
      2
    );

    const fullCode = productM2m.fullCode || productM2m.full_code || "";
    const displayLabel = fullCode || productM2m.label || "";
    // const productIdStr = getProductId(selectedProduct) ?? "";



    const newRows = rows.map((row, i) =>
      i === index
        ? {
          ...row,
          productId: productM2m.uuid || productM2m.id,
          item: displayLabel,
          price,
          quantity: 1,
          priceAsked: price,
          countedPrice: String(countedPricePerUnit),
          tmValue: String(tmValuePerUnit),
        }
        : row
    );

    setRows(newRows);
    onDataChange(newRows.filter((r) => !r.isMedicineAct));
    const newSelectedServices = newRows
      .filter((r) => !r.isMedicineAct)
      .map((row) => row.productId)
      .filter((s) => s !== "");
    setSelectedServices(newSelectedServices);
    calculateTotalClaimed(newRows);
  };

  const handleChange = (index, event) => {
    const { name, value } = event.target;
    const newRows = rows.map((row, i) =>
      i === index ? { ...row, [name]: value } : row
    );
    setRows(newRows);
    onDataChange(newRows.filter((r) => !r.isMedicineAct));
    if (name === "quantity") {
      calculateTotalClaimed(newRows);
    }
  };

  const addRow = () => {
    setRows([
      ...rows,
      {
        productId: "",
        item: "",
        quantity: "",
        price: "",
        tmValue: "",
        countedPrice: "",
        explanation: "",
        priceAsked: "",
      },
    ]);
  };

  const deleteRow = (index) => {
    const newRows = rows.filter((_, i) => i !== index);
    setRows(newRows);
    const newSelectedServices = newRows
      .filter((r) => !r.isMedicineAct)
      .map((row) => row.productId)
      .filter((id) => id !== "");
    setSelectedServices(newSelectedServices);
    calculateTotalClaimed(newRows);
    onDataChange(newRows.filter((r) => !r.isMedicineAct));
  };

  const getProductId = (item) => {
    const id = item?.productM2m?.id ?? item?.productM2m?.uuid;
    return id != null ? String(id) : undefined;
  };

  const getProductOption = (item) => {
    const productM2m = item?.productM2m;
    const id = getProductId(item);
    const fullCode = productM2m?.fullCode || productM2m?.full_code || "";
    const label = fullCode || productM2m?.label || "";
    return {
      ...item,
      label,
      id: id ?? "",
    };
  };

  const getAvailableProducts = (currentProductId) => {
    const currentId = currentProductId != null ? String(currentProductId) : "";
    return filteredServices.filter((item) => {
      const id = getProductId(item);
      if (id == null || id === "") return false;
      return (
        !selectedServices.some((s) => String(s) === id) || id === currentId
      );
    });
  };

  const calculateTotalClaimed = (rows) => {
    const total = rows.reduce(
      (sum, row) =>
        sum + (Number(row.quantity) || 0) * (Number(row.price) || 0),
      0
    );
    const totalTm = rows.reduce((sum, row) => {
      const quantity = row.quantity || 1;
      const tmValue = row.tmValue ? parseFloat(row.tmValue) * quantity : 0;
      return sum + tmValue;
    }, 0);

    const totalSupport = rows.reduce((sum, row) => {
      const quantity = row.quantity || 1;
      const countedPrice = row.countedPrice ? row.countedPrice : null;
      return sum + (countedPrice ? parseFloat(countedPrice) * quantity : 0);
    }, 0);

    setTotalClaimed(total);
    updateTotalClaimed?.(total);
    updateTMvalue?.(totalTm);
    updateSupportTotal?.(totalSupport);
  };

  const totalTmValueRounded = _.round(
    rows.reduce((sum, r) => {
      const quantity = r.quantity || 1;
      const tmValue = r.tmValue ? r.tmValue : null;
      return sum + (tmValue ? parseFloat(tmValue) * quantity : 0);
    }, 0),
    2
  );

  const totalCounterPriceValue = _.round(
    rows.reduce((sum, r) => {
      const quantity = r.quantity || 1;
      const countedPrice = r.countedPrice ? r.countedPrice : null;
      return (
        sum + (countedPrice ? parseFloat(countedPrice) * quantity : 0)
      );
    }, 0),
    2
  );

  const multiplyVal = (value, multiplyBy) => {
    return parseFloat(value || 0) * parseFloat(multiplyBy || 1);
  };

  const headers = () => [
    t("Items"),
    t("Quantity"),
    t("Price"),
    t("Ticket"),
    t("CountedPrice"),
    t("Explanation"),
    t("Action"),
  ];

  const itemFormatters = () => [
    (row, index) => {
      if (row.isMedicineAct) {
        return (
          <TextField
            size="small"
            value={row.item || ""}
            disabled
            sx={{ width: 300 }}
          />
        );
      }
      const rowProductIdStr =
        row.productId != null ? String(row.productId) : "";
      const availableOptions = getAvailableProducts(row.productId)
        .map(getProductOption)
        .filter((opt) => opt.id != null && opt.id !== "");
      const selectedOption = rowProductIdStr
        ? (availableOptions.find(
          (opt) => String(opt.id) === rowProductIdStr
        ) ?? { id: row.productId, label: row.item || "" })
        : null;
      return (
        <Autocomplete
          options={availableOptions}
          getOptionLabel={(opt) => opt?.label ?? ""}
          isOptionEqualToValue={(opt, val) =>
            opt?.id != null && val?.id != null
              ? String(opt.id) === String(val.id)
              : opt === val
          }
          sx={{ width: 300 }}
          size="small"
          value={selectedOption}
          disabled={disabled}
          onChange={(e, option) => {
            handleServiceChange(index, option);
          }}
          renderInput={(params) => <TextField {...params} />}
        />
      );
    },
    (row, index) => (
      <TextField
        type="number"
        className="no-arrow"
        name="quantity"
        size="small"
        value={row.quantity ? row.quantity : ""}
        onChange={(e) => handleChange(index, e)}
        disabled={disabled || row.isMedicineAct}
      />
    ),
    (row) => (
      <TextField
        type="text"
        name="price"
        value={
          row.price != null && row.price !== ""
            ? _.round(
              Number(row.price) * (Number(row.quantity) || 1),
              2
            )
            : ""
        }
        size="small"
        disabled={true}
      />
    ),
    (row) => (
      <TextField
        type="text"
        name="ticket"
        size="small"
        value={
          row.tmValue ? multiplyVal(row.tmValue, row.quantity || 1) : ""
        }
        disabled={true}
      />
    ),
    (row) => (
      <TextField
        type="text"
        name="countedPrice"
        size="small"
        value={
          row.countedPrice
            ? multiplyVal(row.countedPrice, row.quantity || 1)
            : ""
        }
        disabled={true}
      />
    ),
    (row) => (
      <TextField
        type="text"
        name="explanation"
        size="small"
        disabled={true}
        value={row.explanation ? row.explanation : ""}
      />
    ),
    (row, index) =>
      disabled || row.isMedicineAct ? (
        <div />
      ) : (
        <Button
          startIcon={<Delete style={{ color: "red" }} />}
          onClick={() => deleteRow(index)}
        />
      ),
  ];

  const preHeaders = [
    "\u200b",
    "",
    totalClaimed > 0 ? t("Total Claimed: ") + totalClaimed : "",
    totalTmValueRounded > 0 ? t("TotalTm") + totalTmValueRounded : "",
    totalCounterPriceValue > 0
      ? t("TotalCounted") + totalCounterPriceValue
      : "",
    " ",
    " ",
  ];

  return (
    <StyledAccordion>
      <StyledAccordionSummary
        expandIcon={<IndeterminateCheckBoxOutlinedIcon />}
        aria-controls="panel1-content"
        id="panel1-header"
      >
        <Typography variant="h6">{t("MedicalItems")}</Typography>
      </StyledAccordionSummary>
      <AccordionDetails>
        <TableN
          headers={headers()}
          itemFormatters={itemFormatters()}
          preHeaders={preHeaders}
          items={rows}
          withSelection={"single"}
          withPagination={false}
        />
        {!disabled && <Button onClick={addRow}>{t("Add")}</Button>}
      </AccordionDetails>
    </StyledAccordion>
  );
};
